import { apiGet, apiPost, apiPut, type ErrorSubject } from '../../lib/api'
import { withAuth } from '../auth/token'
import type { ComplianceState } from '../brokers/api'
import type { SecurementGear } from '../availability/api'
import type { EquipmentFeature, EquipmentType } from '../carriers/api'
import type { LoadType } from '../availability/api'

/** Mirrors the load slices on the API (Stage 2, 2.2). */
export type LoadSource = 'Dat' | 'Direct'

export const LOAD_SOURCES: readonly LoadSource[] = ['Dat', 'Direct']

/** Open and Expired are the calendar's reading of an open load; Cancelled is history. */
/** A discarded load is only ever read on its own: the book never lists it (TRKC-110 item 4). */
export type LoadStanding = 'Open' | 'Expired' | 'Cancelled' | 'Discarded'

export type StopType = 'Pickup' | 'Delivery'

/** The most stops a load can have — the API refuses more. */
export const MAX_STOPS = 10

export interface LoadStop {
  id: string
  /** 1 for the first stop, in the order the load runs. */
  sequence: number
  type: StopType
  city: string
  state: string
  /** Whether the place resolved on a map; matching says "distance unknown" when not. */
  located: boolean
  company: string | null
  street: string | null
  /** yyyy-MM-dd, a business date. Days, never hours: those come with the rate confirmation. */
  windowFrom: string | null
  /** The last day, inclusive. */
  windowTo: string | null
  referenceNumber: string | null
  notes: string | null
}

/** A way DAT gave to reach the broker about a load: an address, a phone, or a person's both. */
export interface LoadContact {
  email: string | null
  /** As DAT prints it: "(555) 010-0001", and " x404" when the rep has an extension. */
  phone: string | null
}

export interface Load {
  id: string
  source: LoadSource
  /**
   * The broker from the book — null while the load names only the company that offered it:
   * Hugo registers a broker when he books one of its loads, never to capture one (TRKC-104).
   */
  brokerId: string | null
  /** The book's name for the broker, or the company's name as offered while none is named. */
  brokerName: string
  /** The broker's paperwork; null while the load names no broker from the book. */
  brokerComplianceState: ComplianceState | null
  /**
   * False when we stopped working with the broker after the load was captured; true while
   * the load names no broker from the book — nothing about one was ever stood down.
   */
  brokerActive: boolean
  agentId: string | null
  agentName: string | null
  agentEmail: string | null
  agentPhone: string | null
  /**
   * Null when the load names no agent; false when the broker stood the agent down after
   * the load named them — kept on record, and never offered as somebody to call.
   */
  agentActive: boolean | null
  /**
   * What DAT listed to reach the broker about this load, in DAT's order — only a load
   * captured from the paste has any; one typed by hand names an agent instead.
   */
  contacts: LoadContact[]
  /** The type DAT's code names. */
  equipment: EquipmentType
  /** Another type the broker also takes — VR: van or reefer; FD: flatbed or step deck. */
  alternativeEquipment: EquipmentType | null
  /** DAT's code as printed. */
  datEquipmentCode: string | null
  loadType: LoadType
  weightPounds: number
  lengthFeet: number | null
  palletCount: number | null
  tripMiles: number | null
  reeferMinFahrenheit: number | null
  reeferMaxFahrenheit: number | null
  hazmat: boolean
  /** What the unit must have (TRKC-100): the load matches only units that state every one. */
  features: EquipmentFeature[]
  /** What a deck must carry to tie the load down (TRKC-100): DAT's FC asks for chains. */
  securement: SecurementGear[]
  /** The broker's offered total — a rate, never a commission. */
  rateUsd: number | null
  ratePerMileUsd: number | null
  commodity: string | null
  datReference: string | null
  notes: string | null
  stops: LoadStop[]
  standing: LoadStanding
  cancelledAt: string | null
  /** When it was discarded — created by mistake — and by whom (TRKC-110 item 4). */
  discardedAt: string | null
  discardedByEmail: string | null
  createdAt: string
  /** The version an edit is made on — only a save of this load's form moves it. */
  updatedAt: string
}

export interface LoadStopPayload {
  type: StopType
  city: string
  state: string
  company: string | null
  street: string | null
  windowFrom: string | null
  windowTo: string | null
  referenceNumber: string | null
  notes: string | null
}

export interface LoadPayload {
  source: LoadSource
  brokerId: string | null
  /** The company's name, while no broker from the book is named — ignored when one is. */
  brokerCompany: string | null
  agentId: string | null
  equipment: EquipmentType | null
  alternativeEquipment: EquipmentType | null
  datEquipmentCode: string | null
  loadType: LoadType
  weightPounds: number | null
  lengthFeet: number | null
  palletCount: number | null
  tripMiles: number | null
  reeferMinFahrenheit: number | null
  reeferMaxFahrenheit: number | null
  hazmat: boolean
  rateUsd: number | null
  commodity: string | null
  datReference: string | null
  notes: string | null
  stops: LoadStopPayload[]
  features: EquipmentFeature[]
  securement: SecurementGear[]
  /** On an edit, the `updatedAt` the form was opened on. */
  version?: string
}

/** What the book can be narrowed to. Every filter is optional. */
export interface LoadFilter {
  includeCancelled?: boolean
  equipment?: EquipmentType
  pickupState?: string
  brokerId?: string
}

export function listLoads(filter: LoadFilter = {}): Promise<Load[]> {
  const query = new URLSearchParams()

  if (filter.includeCancelled === true) {
    query.set('includeCancelled', 'true')
  }

  for (const key of ['equipment', 'pickupState', 'brokerId'] as const) {
    const value = filter[key]
    if (value !== undefined && value !== '') {
      query.set(key, value)
    }
  }

  const search = query.toString()
  return apiGet<Load[]>(`/api/loads${search === '' ? '' : `?${search}`}`, { headers: withAuth() })
}

export function getLoad(id: string): Promise<Load> {
  return apiGet<Load>(`/api/loads/${id}`, { headers: withAuth() })
}

export function captureLoad(payload: LoadPayload): Promise<Load> {
  return apiPost<Load>('/api/loads', payload, { headers: withAuth() })
}

export function updateLoad(id: string, payload: LoadPayload): Promise<Load> {
  return apiPut<Load>(`/api/loads/${id}`, payload, { headers: withAuth() })
}

export function cancelLoad(id: string): Promise<Load> {
  return apiPut<Load>(`/api/loads/${id}/cancellation`, undefined, { headers: withAuth() })
}

/**
 * Loads created by mistake (TRKC-110 item 4): out of the book, matching and the duplicate check,
 * kept as a record of who and when, never back. One from its page, several from the book — all
 * this tenant's, or none goes.
 */
/** The most loads one discard names — the API's `DiscardLoadsHandler.MaxLoads`, mirrored for the book. */
export const MAX_DISCARD = 500

export function discardLoads(ids: readonly string[]): Promise<{ discarded: number }> {
  return apiPost<{ discarded: number }>('/api/loads/discards', { loadIds: ids }, { headers: withAuth() })
}

/**
 * Reading a DAT search the dispatcher copied (Stage 2, 2.2). The API writes nothing
 * here: it reads the clipboard text back as rows, and the screen is where a person
 * approves or corrects them.
 */
export interface DatPasteRow {
  number: number
  age: string | null
  rate: number | null
  ratePerMile: number | null
  tripMiles: number | null
  originCity: string | null
  originState: string | null
  deadheadToOrigin: number | null
  destinationCity: string | null
  destinationState: string | null
  deadheadFromDestination: number | null
  pickUpFrom: string | null
  pickUpTo: string | null
  /** DAT's own code, verbatim — "V", "VR", "FD", "VA". */
  equipmentCode: string | null
  /** The base type the code names, read by Hugo's table (TRKC-100). */
  equipment: EquipmentType | null
  /** The type the code names as also taken — VR: a reefer; FD: a step deck. */
  alternativeEquipment: EquipmentType | null
  /** What the code says the unit must have — VA: air ride. */
  features: EquipmentFeature[]
  /** What the code says a deck must carry — FC: chains. */
  securement: SecurementGear[]
  /** The code's Z: the load is hazardous material. */
  hazmat: boolean
  weightPounds: number | null
  lengthFeet: number | null
  loadType: LoadType | null
  company: string | null
  contactEmail: string | null
  contactPhone: string | null
  creditScore: number | null
  daysToPay: number | null
  isCancelled: boolean
  isBookable: boolean
  /** Whether the row says where, when and from whom — what a load needs to exist. */
  isComplete: boolean
  /** Values that belonged to no column: a detail panel left open, usually. */
  unread: string[]
  /**
   * Values in the middle of the row that no column takes — something the reader does not
   * know. While there are any, the row is not ready (TRKC-93).
   */
  skipped: string[]
  /** The rows that were this same load, posted by other reps, merged into it (decision 1 of the capture). */
  mergedRows: number[]
  /** The contacts those rows carried, beyond this row's own. */
  otherContacts: LoadContact[]
  /** The first row of the same broker on the same route, when this one differs from it. */
  sameLaneAs: number | null
}

/**
 * Something the reader could not fit into a row: a code this portal translates, and the
 * values its sentence interpolates. The API never sends the sentence — a note is often
 * the only explanation a paste gets, and it has to arrive in the reader's language.
 */
export interface DatPasteNote {
  code: string
  values: Record<string, string>
}

export interface DatPasteSummary {
  /** Every row DAT listed, the merged ones included. */
  read: number
  /** Rows that were the same load posted again by another rep, merged into its first row. */
  merged: number
  ready: number
  cancelled: number
  incomplete: number
}

export interface DatPasteReading {
  rows: DatPasteRow[]
  notes: DatPasteNote[]
  summary: DatPasteSummary
}

export const readDatPaste = (text: string): Promise<DatPasteReading> =>
  apiPost<DatPasteReading>('/api/loads/dat-paste', { text }, { headers: withAuth() })

/** One company as the paste names it, with every way DAT gave to reach it. */
export interface DatBrokerQuery {
  /** The screen's own name for the company's rows, echoed back by the answer. */
  key: string
  company: string
  contacts: LoadContact[]
}

/**
 * How a company resolved in the broker book. Known needs nobody's judgement; the rest
 * are confirmed by the dispatcher before anything is captured.
 */
export type DatBrokerStatus = 'Known' | 'Ambiguous' | 'Candidate' | 'Suggested' | 'Unknown'

/** What pointed at a broker: one of its agents' contacts, its own, one a capture learned, a domain, a name. */
export type DatBrokerFoundBy = 'Agent' | 'Broker' | 'Learned' | 'Domain' | 'Name'

export interface DatBrokerOption {
  brokerId: string
  name: string
  mcNumber: string | null
  complianceState: ComplianceState
  foundBy: DatBrokerFoundBy
  /** The contact or the domain that matched; null for a name. */
  evidence: string | null
  agentName: string | null
}

export interface DatBrokerResolution {
  key: string
  status: DatBrokerStatus
  brokers: DatBrokerOption[]
  /** Brokers we stopped working with that the same evidence points at — never offered, only said. */
  stoodDown: DatBrokerOption[]
}

export const resolveDatBrokers = (companies: DatBrokerQuery[]): Promise<{ companies: DatBrokerResolution[] }> =>
  apiPost<{ companies: DatBrokerResolution[] }>('/api/loads/dat-paste/brokers', { companies }, { headers: withAuth() })

/**
 * One row as the dispatcher approved it: with the broker they chose from the book, or —
 * with none — under the company's name as DAT printed it (TRKC-104).
 */
export interface DatCaptureRow {
  number: number
  /** What the reading said: a row DAT had cancelled is refused by the capture, not only left unticked here. */
  isCancelled: boolean
  brokerId: string | null
  /** The company's name as the row stands; the capture files it only while no broker is named. */
  company: string | null
  originCity: string | null
  originState: string | null
  destinationCity: string | null
  destinationState: string | null
  pickUpFrom: string | null
  pickUpTo: string | null
  equipment: EquipmentType | null
  alternativeEquipment: EquipmentType | null
  equipmentCode: string | null
  loadType: LoadType | null
  weightPounds: number | null
  lengthFeet: number | null
  tripMiles: number | null
  rate: number | null
  /** Every contact DAT listed for the load, in DAT's order. */
  contacts: LoadContact[]
  notes: string | null
  /** What the row asks of the unit, as read and corrected (TRKC-100). */
  features: EquipmentFeature[]
  securement: SecurementGear[]
  hazmat: boolean
  /** The temperatures the dispatcher typed: DAT prints none, and a reefer load states them (TRKC-110). */
  reeferMinFahrenheit: number | null
  reeferMaxFahrenheit: number | null
}

export interface DatCaptureError {
  code: string
  message: string
  subject: ErrorSubject | null
}

/** One row's answer: the load it became, or every reason it did not. */
export interface DatCaptureOutcome {
  number: number
  loadId: string | null
  errors: DatCaptureError[]
}

export interface DatCaptureResult {
  rows: DatCaptureOutcome[]
  captured: number
  refused: number
}

export const captureDatRows = (rows: DatCaptureRow[]): Promise<DatCaptureResult> =>
  apiPost<DatCaptureResult>('/api/loads/dat-paste/capture', { rows }, { headers: withAuth() })
